/**
 * Arena hazards: the thing that makes a stage more than a wall pattern.
 *
 * Six arenas that differ only in where the pillars are is six versions of one
 * board, and the layouts had reached the point where telling them apart during
 * play meant reading the colours. A hazard changes what you *do* on a board
 * rather than where you can walk on it.
 *
 * Two decisions hold this whole file up.
 *
 * **It is an overlay, not a tile kind.** `GameState` already carries `grid` and
 * `items` as flat arrays parallel to the board, so a third is the established
 * shape rather than a new idea. It also matters practically: sixty-nine places
 * across thirteen files switch on `Tile`, and every one of them would have
 * needed auditing for a new tile value. None of them changes for an overlay.
 *
 * Hazard schedules derive from the tick and fixed map overlay. Player carry
 * velocity and expiry travel in snapshots so mid-launch prediction can resume.
 */
import { TICK_HZ } from './constants';

export const Hazard = {
  NONE: 0,
  /** Periodically live. Standing on it while it is costs a hit. */
  WIRE: 1,
  /** Periodically erupts, throwing whatever is standing on it clear. */
  VENT: 2,
  FAN_UP: 3,
  FAN_DOWN: 4,
  FAN_LEFT: 5,
  FAN_RIGHT: 6,
  /** Ice. You keep going after you stop asking to, and so do bombs. */
  SLICK: 7,
  /** Undergrowth. Slower going, for whoever is in it. */
  MIRE: 8,
} as const;
export type HazardValue = (typeof Hazard)[keyof typeof Hazard];

/**
 * How an arena writes them down.
 *
 * Deliberately pictorial: `~` is a cable, `*` is a burst, the arrows point the
 * way the air moves. A layout is edited by eye far more often than it is
 * parsed, and the existing legend (`#` block, `.` floor, `_` bare lane) set
 * that expectation.
 *
 * Every one of these lands in `buildMap`'s `else` branch, which makes them open
 * floor that is *not* eligible for a soft block -- correct without asking, since
 * a crate sitting on a vent is a hazard the player cannot see.
 */
export const HAZARD_CHARS: Readonly<Record<string, HazardValue>> = {
  '~': Hazard.WIRE,
  '*': Hazard.VENT,
  '^': Hazard.FAN_UP,
  v: Hazard.FAN_DOWN,
  '<': Hazard.FAN_LEFT,
  '>': Hazard.FAN_RIGHT,
  '=': Hazard.SLICK,
  ',': Hazard.MIRE,
};

/** The push direction of a fan, or null for everything else. */
export function fanVector(h: number): readonly [number, number] | null {
  switch (h) {
    case Hazard.FAN_UP:
      return [0, -1];
    case Hazard.FAN_DOWN:
      return [0, 1];
    case Hazard.FAN_LEFT:
      return [-1, 0];
    case Hazard.FAN_RIGHT:
      return [1, 0];
    default:
      return null;
  }
}

/**
 * Cycle lengths, in ticks.
 *
 * The shape each one is aiming for: long enough off that crossing is a
 * decision rather than a dice roll, long enough on that it cannot be walked
 * through by accident, and a warning window before it bites so that being hit
 * is always something you could have read.
 */
const WIRE_PERIOD = Math.round(4.4 * TICK_HZ);
const WIRE_LIVE = Math.round(1.3 * TICK_HZ);
const VENT_PERIOD = Math.round(5.2 * TICK_HZ);
const VENT_LIVE = Math.round(0.4 * TICK_HZ);
const FAN_PERIOD = Math.round(5.0 * TICK_HZ);
const FAN_LIVE = Math.round(2.2 * TICK_HZ);
/** How long before it fires that a hazard starts telling you. */
const WARN = Math.round(0.7 * TICK_HZ);

/**
 * Where in its cycle a cell is, 0 to period.
 *
 * Phase is derived from the cell index rather than shared, so a room full of
 * wires ripples instead of blinking in unison -- which is both better looking
 * and better play, because a board where everything is safe at once and lethal
 * at once has one decision on it rather than many.
 *
 * The multiplier is coprime-ish with the periods so neighbours land far apart
 * in the cycle rather than a tile or two along.
 */
function phase(cell: number, tick: number, period: number): number {
  return (tick + cell * 37) % period;
}

/** Is this hazard cell dangerous *right now*? */
export function hazardLive(kind: number, cell: number, tick: number): boolean {
  if (kind === Hazard.WIRE) return phase(cell, tick, WIRE_PERIOD) < WIRE_LIVE;
  if (kind === Hazard.VENT) return phase(cell, tick, VENT_PERIOD) < VENT_LIVE;
  if (fanVector(kind)) return phase(cell, tick, FAN_PERIOD) < FAN_LIVE;
  // Ice and undergrowth are terrain, not events: always on, never lethal.
  return false;
}

/**
 * Is it about to be?
 *
 * For the renderer and for the bots, which both need to know a beat early --
 * one to warn the player, the other so it does not path onto a wire that comes
 * alive under it. Being able to see a hazard arm is the difference between a
 * hazard and a random punishment.
 */
export function hazardArming(kind: number, cell: number, tick: number): boolean {
  if (hazardLive(kind, cell, tick)) return false;
  const period = kind === Hazard.WIRE ? WIRE_PERIOD : kind === Hazard.VENT ? VENT_PERIOD : FAN_PERIOD;
  if (kind !== Hazard.WIRE && kind !== Hazard.VENT && !fanVector(kind)) return false;
  return phase(cell, tick, period) >= period - WARN;
}

/** Only these can hurt you; the rest change how you move. */
export function hazardHarms(kind: number): boolean {
  return kind === Hazard.WIRE;
}

/** Speed multiplier for standing on this hazard. 1 leaves the player alone. */
export function hazardSpeed(kind: number): number {
  if (kind === Hazard.MIRE) return 0.62;
  if (kind === Hazard.SLICK) return 1.18;
  return 1;
}

/* -------------------------------------------------------------------------- */
/* Tuning                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Ice, as a carry rather than a friction model.
 *
 * A real friction model would need acceleration on the player, which nothing
 * else in this game has -- movement is "you are going at your speed or you are
 * not". Refreshing a short carry while there is input and spending it when
 * there is none produces the part of ice that matters in play: you arrive
 * late, and stopping is something you have to plan.
 */
export const SLIDE_TICKS = Math.round(0.34 * TICK_HZ);
/** How much of walking speed the slide keeps. Under 1: ice is not a boost. */
export const SLIDE_FACTOR = 0.85;

/** Tiles/second a running fan drags a player. Well under a walk. */
export const FAN_PUSH = 1.9;
/** A fan's shove on a bomb, as a fraction of `KICK_SPEED`. */
export const FAN_BOMB = 0.55;
/**
 * Ticks between a fan nudging a pickup one cell.
 *
 * At tick rate a power-up would cross the board faster than anybody could
 * chase it, which deletes the item rather than relocating it.
 */
export const ITEM_DRIFT_TICKS = Math.round(0.5 * TICK_HZ);

/** How long a vent's throw lasts, and how fast. */
export const LAUNCH_TICKS = Math.round(0.42 * TICK_HZ);
export const LAUNCH_SPEED = 9.5;
